import type React from 'react'
import { ApiError, EmailInUseError, NetworkError, ValidationError, VersionMismatchError } from '../../api/client'
import type { AppAction, ToastInput } from '../../types/app'

export function showToast(dispatch: React.Dispatch<AppAction>, message: string, extra: Omit<ToastInput, 'message'> = {}): void {
  dispatch({ type: 'SHOW_TOAST', payload: { message, ...extra } })
}

// reloadPage reloads the app, keeping her place (whereAt): the way on from
// "This changed on another device".
export function reloadPage(): void {
  window.location.reload()
}

// showSaveError puts a failed save in an error toast; a save that lost to
// another device offers Reload.
export function showSaveError(dispatch: React.Dispatch<AppAction>, error: unknown): void {
  showToast(dispatch, saveErrorMessage(error), {
    tone: 'error',
    action: error instanceof VersionMismatchError ? { label: 'Reload', run: reloadPage } : undefined,
  })
}

// saveErrorMessage explains a failed save that isn't a validation problem.
export function saveErrorMessage(error: unknown): string {
  if (error instanceof NetworkError) {
    return "Couldn’t reach Harmovi. Check your connection and try again."
  }
  if (error instanceof ApiError && error.status === 401) {
    return "You’ve been signed out. Sign in again."
  }
  if (error instanceof VersionMismatchError) {
    return 'This changed on another device. Reload to see the latest.'
  }
  if (error instanceof EmailInUseError) {
    return `${error.clientName} already has this email.`
  }
  if (error instanceof ApiError && error.problem === 'email-confirmed') {
    return 'This email is confirmed. To change it, email hello@harmovi.com.'
  }
  if (error instanceof ApiError && error.problem === 'card-provider-unavailable') {
    return 'Card payments are busy right now. Try again in a minute.'
  }
  if (error instanceof ApiError && error.problem === 'card-provider-refused') {
    return "Card payments couldn’t do that just now. It isn’t something you did. If it keeps happening, email hello@harmovi.com."
  }
  // The plan routes (POST /v1/billing/…), in the API's own words.
  if (error instanceof ApiError && error.problem === 'billing-not-configured') {
    return 'Plans aren’t available right now. Try again later.'
  }
  if (error instanceof ApiError && error.problem === 'already-on-rung') {
    return 'You already have this.'
  }
  if (error instanceof ApiError && error.problem === 'no-subscription') {
    return 'You have no subscription to change.'
  }
  if (error instanceof ApiError && error.problem === 'payment-needed') {
    return 'Your card was declined. Update it in Card and invoices, then try again.'
  }
  if (error instanceof ApiError && error.problem === 'coach-required') {
    return 'This is part of Pro.'
  }
  if (error instanceof ApiError && error.problem === 'photos-unavailable') {
    return "Photos can’t be uploaded right now. Try again later."
  }
  if (error instanceof ApiError && error.problem === 'no-deposit-rule') {
    return 'Set a deposit in Settings → Booking page first.'
  }
  // A package that covers nothing on her menu any more is not sold.
  if (error instanceof ValidationError && error.fields.package_id) {
    return error.fields.package_id
  }
  if (error instanceof ApiError && error.problem === 'no-client-email') {
    return "Add the client’s email first, so we can send the link."
  }
  if (error instanceof ApiError && error.problem === 'charge-pending') {
    return 'A card charge is still being checked. Wait for it to finish.'
  }
  if (error instanceof ApiError && error.problem === 'not-chargeable') {
    return 'This can’t be charged to their card right now.'
  }
  if (error instanceof ApiError && error.problem === 'no-saved-card') {
    return 'There’s no saved card for this client any more.'
  }
  return 'Something went wrong. Try again.'
}
